/**
 * Dicionário único, com as duas línguas lado a lado.
 *
 * Manter os pares juntos é de propósito: se um idioma ganha uma chave, o outro
 * não passa despercebido — o TypeScript exige os dois.
 */
export interface Message {
  pt: string;
  en: string;
}

export const messages = {
  // ── Cabeçalho ─────────────────────────────────────────────────────────────
  'app.name': { pt: 'DMS SKINS', en: 'DMS SKINS' },
  'app.tagline': { pt: 'skins de CS2', en: 'CS2 skins' },
  'app.breadcrumbHint': {
    pt: 'categorias → skins → desgastes → anúncios',
    en: 'categories → skins → wears → listings',
  },
  'header.watchlist': { pt: 'Favoritos', en: 'Favorites' },
  'header.language': { pt: 'Idioma', en: 'Language' },
  'header.currency': { pt: 'Moeda', en: 'Currency' },

  // ── Estado do serviço ─────────────────────────────────────────────────────
  'status.demo': { pt: 'modo demo', en: 'demo mode' },
  'status.demoTitle': {
    pt: 'Sem credenciais válidas do Youpin. Os dados são de exemplo.',
    en: 'No valid Youpin credentials. The data shown is sample data.',
  },
  'status.credentialsExpired': { pt: 'credenciais expiradas', en: 'credentials expired' },
  'status.youpinOk': { pt: 'youpin ok', en: 'youpin ok' },
  'status.youpinOkTitle': { pt: 'Credenciais aceitas pelo Youpin', en: 'Credentials accepted by Youpin' },
  'status.dbOk': { pt: 'banco ok', en: 'db ok' },
  'status.dbOff': { pt: 'sem banco', en: 'no db' },
  'status.dbOkTitle': {
    pt: 'Postgres conectado: histórico e watchlist ativos',
    en: 'Postgres connected: history and watchlist enabled',
  },
  'status.dbOffTitle': {
    pt: 'Sem Postgres: cache só em memória, sem histórico nem watchlist',
    en: 'No Postgres: memory-only cache, no history or watchlist',
  },
  'status.queue': { pt: 'fila', en: 'queue' },
  'status.queueTitle': {
    pt: 'Chamadas em andamento / na fila para o Youpin',
    en: 'In-flight / queued calls to Youpin',
  },

  // ── Filtros (painel estilo Buff) ──────────────────────────────────────────
  'filters.category': { pt: 'Categoria', en: 'Category' },
  'filters.weapon': { pt: 'Arma', en: 'Weapon' },
  'filters.exterior': { pt: 'Desgaste', en: 'Exterior' },
  'filters.rarity': { pt: 'Raridade', en: 'Rarity' },
  'filters.quality': { pt: 'Qualidade', en: 'Quality' },
  'filters.all': { pt: 'Todos', en: 'All' },
  'filters.allFem': { pt: 'Todas', en: 'All' },
  'filters.expand': { pt: 'Expandir', en: 'Expand' },
  'filters.collapse': { pt: 'Recolher', en: 'Collapse' },
  'filters.clear': { pt: 'Limpar filtros', en: 'Clear filters' },
  'filters.price': { pt: 'Preço', en: 'Price' },
  'filters.color': { pt: 'Cor', en: 'Color' },
  'filters.stattrak': { pt: 'StatTrak™', en: 'StatTrak™' },
  'filters.team': { pt: 'Lado', en: 'Side' },
  'filters.teamCt': { pt: 'CT', en: 'CT' },
  'filters.teamTr': { pt: 'TR', en: 'TR' },
  'filters.yes': { pt: 'Sim', en: 'Yes' },
  'filters.no': { pt: 'Não', en: 'No' },
  'filters.priceMin': { pt: 'Mínimo', en: 'Min' },
  'filters.priceMax': { pt: 'Máximo', en: 'Max' },
  'filters.priceApply': { pt: 'Aplicar', en: 'Apply' },
  'filters.priceAny': { pt: 'Qualquer', en: 'Any' },
  'filters.priceAbove': { pt: 'Acima de {min}', en: 'Above {min}' },
  'filters.priceFloor': {
    pt: 'Skins abaixo de {value} não são exibidas.',
    en: 'Skins below {value} are not shown.',
  },
  'filters.unvalidated': {
    pt: 'código de filtro ainda não validado contra o Youpin',
    en: 'filter code not yet validated against Youpin',
  },

  // ── Nível 2 ───────────────────────────────────────────────────────────────
  'level2.title': { pt: 'skins', en: 'skins' },
  'crumbs.market': { pt: 'Mercado', en: 'Market' },
  'level2.searchPlaceholder': { pt: 'Filtrar skins por nome…', en: 'Filter skins by name…' },
  'level2.groupBySkin': { pt: 'Agrupar por skin', en: 'Group by skin' },
  'level2.reload': { pt: 'Recarregar', en: 'Reload' },
  'level2.reloading': { pt: 'Atualizando…', en: 'Refreshing…' },
  'level2.countGrouped': { pt: '{skins} skins · {variants} variações', en: '{skins} skins · {variants} variants' },
  'level2.countFlat': { pt: '{n} variações nesta página', en: '{n} variants on this page' },
  'level2.wholeCategory': { pt: 'categoria inteira', en: 'whole category' },
  'level2.fromPrice': { pt: 'a partir de', en: 'from' },
  'level2.wearsCount': { pt: '{n} desgaste', en: '{n} wear' },
  'level2.wearsCountPlural': { pt: '{n} desgastes', en: '{n} wears' },
  'level2.onSale': { pt: '{n} à venda', en: '{n} on sale' },

  // ── Ordenação ─────────────────────────────────────────────────────────────
  'sort.priceAsc': { pt: 'Preço ↑', en: 'Price ↑' },
  'sort.priceDesc': { pt: 'Preço ↓', en: 'Price ↓' },
  'sort.discount': { pt: 'Maior desconto vs Steam', en: 'Biggest discount vs Steam' },
  'sort.onsale': { pt: 'Mais à venda', en: 'Most on sale' },
  'sort.name': { pt: 'Nome A→Z', en: 'Name A→Z' },
  'sort.floatAsc': { pt: 'Float ↑ (menor primeiro)', en: 'Float ↑ (lowest first)' },
  'sort.floatDesc': { pt: 'Float ↓', en: 'Float ↓' },
  'sort.seed': { pt: 'Pattern', en: 'Pattern' },

  // ── Nível 2.5 ─────────────────────────────────────────────────────────────
  'level25.title': { pt: 'desgastes', en: 'wears' },
  'level25.loading': { pt: 'Carregando desgastes…', en: 'Loading wears…' },
  'level25.listings': { pt: 'Anúncios', en: 'Listings' },
  'level25.empty': {
    pt: 'Nenhum desgaste disponível para esta skin no momento.',
    en: 'No wears available for this skin right now.',
  },
  'level25.unknownWear': { pt: 'Desgaste desconhecido', en: 'Unknown wear' },
  'level25.summary': {
    pt: '{rarity} · {count} desgaste(s) · {onSale} à venda',
    en: '{rarity} · {count} wear(s) · {onSale} on sale',
  },

  // ── Nível 3 ───────────────────────────────────────────────────────────────
  'level3.title': { pt: 'anúncios', en: 'listings' },
  'level3.loading': { pt: 'Buscando anúncios…', en: 'Fetching listings…' },
  'level3.empty': {
    pt: 'Nenhum anúncio à venda para este desgaste no momento.',
    en: 'No listings on sale for this wear right now.',
  },
  'level3.top': { pt: 'Top {n}', en: 'Top {n}' },
  'level3.allOnPage': { pt: 'Todos da página', en: 'All on page' },
  'level3.history': { pt: 'Histórico de preço', en: 'Price history' },
  'level3.hideHistory': { pt: 'Ocultar histórico', en: 'Hide history' },
  'level3.watch': { pt: 'Acompanhar', en: 'Watch' },
  'level3.watching': { pt: 'Na watchlist', en: 'Watching' },
  'level3.saving': { pt: 'Salvando…', en: 'Saving…' },
  'level3.refresh': { pt: 'Atualizar', en: 'Refresh' },
  'level3.shown': { pt: '{n} exibidos', en: '{n} shown' },
  'level3.filtered': {
    pt: '{m} de {s} anúncios passaram nos filtros',
    en: '{m} of {s} listings match the filters',
  },
  'level3.scannedPages': {
    pt: '{n} página(s) varrida(s)',
    en: '{n} page(s) scanned',
  },
  'level3.scanTruncated': {
    pt: 'Esta skin tem anúncios demais e a varredura parou no limite: pode haver mais peças com esse estilo além das listadas. Estreite o filtro (float ou pattern) para chegar nelas.',
    en: 'This skin has too many listings and the scan hit its limit: there may be more items with this style beyond the ones listed. Narrow the filter (float or pattern) to reach them.',
  },
  'level3.ofTotal': { pt: '{n} no total', en: '{n} total' },
  'level3.float': { pt: 'Float', en: 'Float' },
  'level3.pattern': { pt: 'Pattern', en: 'Pattern' },
  'level3.withNameTag': { pt: 'com etiqueta', en: 'name tagged' },
  'level3.image': { pt: 'Imagem', en: 'Image' },
  'level3.zoom': { pt: 'Ver a imagem maior', en: 'View larger image' },
  'level3.fadePct': { pt: '% de Fade', en: 'Fade %' },
  'level3.tier': { pt: 'Tier do Case Hardened', en: 'Case Hardened tier' },
  'level3.inGame': { pt: 'Jogo', en: 'In game' },
  'level3.inspectTitle': {
    pt: 'Abrir no CS2 e inspecionar (um item por vez)',
    en: 'Open in CS2 and inspect (one item at a time)',
  },
  'level3.noInspect': {
    pt: 'Este anúncio não trouxe link de inspeção',
    en: 'This listing has no inspect link',
  },
  'level3.realImage': { pt: 'Ver a foto real desta peça', en: 'View the real photo of this item' },
  'level3.modelImage': {
    pt: 'Sem foto da peça. Esta é a imagem do modelo.',
    en: 'No item photo. This is the model image.',
  },
  'level3.modelImageCaption': { pt: 'imagem do modelo', en: 'model image' },
  'level3.sticker': { pt: 'adesivo', en: 'sticker' },
  'level3.charm': { pt: 'pingente', en: 'charm' },
  'level3.stickerWear': { pt: 'desgaste {wear}', en: 'wear {wear}' },

  // ── Watchlist ─────────────────────────────────────────────────────────────
  'watchlist.title': { pt: 'Watchlist', en: 'Watchlist' },
  'watchlist.loading': { pt: 'Carregando watchlist…', en: 'Loading watchlist…' },
  'watchlist.empty': { pt: 'Nada na watchlist ainda.', en: 'Nothing in the watchlist yet.' },
  'watchlist.emptyHint': {
    pt: 'Abra uma skin no Nível 3 e clique em ☆ Acompanhar.',
    en: 'Open a skin at Level 3 and click ☆ Watch.',
  },
  'watchlist.alerts': {
    pt: '{n} item(ns) atingiram o preço-alvo: {names}',
    en: '{n} item(s) hit the target price: {names}',
  },
  'watchlist.addedAt': { pt: 'adicionado em {date}', en: 'added on {date}' },
  'watchlist.target': { pt: 'alvo', en: 'target' },
  'watchlist.hit': { pt: 'atingido', en: 'hit' },
  'watchlist.remove': { pt: 'Remover', en: 'Remove' },

  // ── Estados e erros ───────────────────────────────────────────────────────
  'common.retry': { pt: 'Tentar de novo', en: 'Try again' },
  'common.close': { pt: 'Fechar', en: 'Close' },
  'common.previous': { pt: 'Anterior', en: 'Previous' },
  'common.next': { pt: 'Próxima', en: 'Next' },
  'common.page': { pt: 'página {n}', en: 'page {n}' },
  'common.pageOf': { pt: 'página {n} de {total}', en: 'page {n} of {total}' },
  'common.loading': { pt: 'Carregando…', en: 'Loading…' },
  'common.steam': { pt: 'Steam', en: 'Steam' },
  'common.noImage': { pt: 'sem imagem', en: 'no image' },
  'common.points': { pt: '{n} pontos · {days} dias', en: '{n} points · {days} days' },

  'error.title': { pt: 'Não foi possível carregar', en: 'Could not load' },
  'error.backend': {
    pt: 'Não foi possível falar com o backend ({message}). Confirme que a API está rodando em {url}.',
    en: 'Could not reach the backend ({message}). Make sure the API is running at {url}.',
  },
  'error.credentialsExpired': {
    pt: 'As credenciais do Youpin expiraram em {date}. Capture novas no DevTools e envie para POST /api/admin/credentials com o header x-admin-token.',
    en: 'Youpin credentials expired on {date}. Capture new ones in DevTools and POST them to /api/admin/credentials with the x-admin-token header.',
  },
  'error.notLoggedIn': {
    pt: 'Se acabou de fazer várias buscas seguidas, espere um minuto e tente de novo. Se persistir, faça login em youpin898.com e recapture os headers uk e deviceuk já logado.',
    en: 'If you just ran many searches in a row, wait a minute and retry. If it persists, log in at youpin898.com and re-capture the uk and deviceuk headers while logged in.',
  },
  'error.handshake': {
    pt: 'Apesar da mensagem citar a versão do app, isso quer dizer que os headers uk, deviceuk e deviceid não foram aceitos juntos. Confira os três no .env.',
    en: 'Despite the message mentioning app version, this means the uk, deviceuk and deviceid headers were not accepted together. Check all three in .env.',
  },
  'error.apiError': {
    pt: 'O Youpin recusou o formato do body. Capture a requisição real no DevTools e fixe o formato em POST /api/admin/listing-body-template.',
    en: 'Youpin rejected the request body format. Capture the real request in DevTools and pin the format via POST /api/admin/listing-body-template.',
  },
  'error.pageNotFound': { pt: 'Página não encontrada.', en: 'Page not found.' },

  // ── Vazio no Nível 2 ──────────────────────────────────────────────────────
  'empty.noSkins': { pt: 'Ainda não há nenhuma skin de {weapon} no catálogo.', en: 'No {weapon} skins in the catalog yet.' },
  'empty.withSearch': {
    pt: 'Nenhuma skin de {weapon} no catálogo com o filtro “{search}”.',
    en: 'No {weapon} skins in the catalog matching “{search}”.',
  },
  'empty.unvalidatedCode': {
    pt: 'O código de filtro desta arma ({code}) ainda não foi validado contra o Youpin, e ele respondeu vazio. Provavelmente é outro. Abra a categoria correspondente em youpin898.com com o DevTools → Network, veja o filterMap.Type que o site envia, e corrija em apps/api/src/youpin/taxonomy.ts.',
    en: 'This weapon’s filter code ({code}) has not been validated against Youpin, and it returned empty. It is probably a different one. Open the matching category on youpin898.com with DevTools → Network, check the filterMap.Type it sends, and fix it in apps/api/src/youpin/taxonomy.ts.',
  },
  'empty.guestSession': {
    pt: 'A sessão do Youpin está como visitante: nesse estado a API não filtra por arma nem pagina. Faça login em youpin898.com e recapture os headers uk, deviceuk e authorization já logado.',
    en: 'The Youpin session is being treated as a guest: in that state the API neither filters by weapon nor paginates. Log in at youpin898.com and re-capture the uk, deviceuk and authorization headers while logged in.',
  },
  'empty.nothingOnSale': {
    pt: 'O Youpin não tem nenhuma skin desta arma à venda no momento, ou ela ainda não foi coletada. Tente Recarregar.',
    en: 'Youpin has no skins for this weapon on sale right now, or it has not been collected yet. Try Reload.',
  },
  'empty.level3Works': {
    pt: 'O Nível 3 já funciona com dados reais. Se você souber o templateId de uma skin, veja os anúncios agora:',
    en: 'Level 3 already works with real data. If you know a skin’s templateId, see its listings now:',
  },
  'empty.templateIdExample': {
    pt: 'Exemplo: 1414 = AK-47 | Redline (Testada em Campo) · 407 = AWP | Asiimov.',
    en: 'Example: 1414 = AK-47 | Redline (Field-Tested) · 407 = AWP | Asiimov.',
  },
  'empty.selectSomething': {
    pt: 'Escolha uma categoria ou uma arma no menu acima.',
    en: 'Pick a category or a weapon in the menu above.',
  },
  'empty.goToListings': { pt: 'Ver anúncios', en: 'View listings' },
  'empty.templateIdPlaceholder': { pt: 'templateId…', en: 'templateId…' },

  // ── Varredura do catálogo ─────────────────────────────────────────────────
  'sync.start': { pt: 'Atualizar catálogo', en: 'Update catalog' },
  'sync.cancel': { pt: 'Cancelar', en: 'Cancel' },
  'sync.running': {
    pt: 'Varrendo o mercado · página {page} de {total} · {items} itens',
    en: 'Scanning the market · page {page} of {total} · {items} items',
  },
  'sync.lastRun': { pt: 'Catálogo: {items} itens · {date}', en: 'Catalog: {items} items · {date}' },
  'sync.never': { pt: 'Catálogo ainda não foi montado', en: 'Catalog not built yet' },
  'sync.emptyHint': {
    pt: 'O catálogo está vazio para este tipo. Clique em "Atualizar catálogo" para varrer o mercado inteiro (leva alguns minutos e só precisa ser feito de vez em quando).',
    en: 'The catalog is empty for this type. Click “Update catalog” to scan the whole market (it takes a few minutes and only needs doing occasionally).',
  },

  // ── Câmbio ────────────────────────────────────────────────────────────────
  'fx.rate': { pt: '1 CNY = {rate} BRL', en: '1 CNY = {rate} BRL' },
  'fx.source': { pt: 'câmbio Wise', en: 'Wise rate' },
  'fx.updated': { pt: 'atualizado {time}', en: 'updated {time}' },
  'fx.stale': {
    pt: 'A Wise não respondeu; usando a última cotação conhecida.',
    en: 'Wise did not respond; using the last known rate.',
  },
  'fx.unavailable': {
    pt: 'Câmbio indisponível. Os preços estão em yuan.',
    en: 'Exchange rate unavailable. Prices are shown in yuan.',
  },
  'fx.convertedFrom': { pt: 'convertido de {value}', en: 'converted from {value}' },
} as const satisfies Record<string, Message>;

export type MessageKey = keyof typeof messages;
export type Lang = 'pt' | 'en';
